import React from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Paper,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  locationsApi,
  locationAssignmentsApi,
  beaconAssignmentsApi,
  edgeAccountDigestsApi,
  organizationsApi,
  hexahqCacheApi,
} from '../api/services';
import type { HexahqAccount } from '../api/services';
import { extractApiError } from '../utils/apiErrors';
import { useHexahqAutocomplete } from '../hooks/useHexahqAutocomplete';
import { API_CONFIG, ENV } from '../api/config';
import { EmailPreviewModal } from '../components/edge-account-digests/EmailPreviewModal';
import { StatusMonitoringFields } from '../components/locations/StatusMonitoringFields';
import { DigestRecipientFields, restockCadenceErrors } from '../components/locations/DigestRecipientFields';
import { DigestPreferencesFields } from '../components/locations/DigestPreferencesFields';
import { FulfillmentFields } from '../components/locations/FulfillmentFields';
import { AccountManagerFields } from '../components/locations/AccountManagerFields';
import type { InventoryPeGroup, InventoryUngroupedItem, InventoryStatus, LocationDevice, DeviceFreshness, LocationAssignment, BeaconAssignment, EdgeAccountDigest, OpenQuote, Location, LocationOnboarding, DigestPreferences, LocationHvaRateResponse, LocationEmailOpensResponse, HvaConversion, HvaSparkBucket, WarehouseInventoryRow } from '../api/config';
import { FulfillmentOrderCard, SkuOrderDetail, fmtDateTime, OrderLink, QuoteLink } from '../components/locations/OrderFulfillment';
import type { LocationOrderFulfillmentResponse } from '../api/config';
import { onboardingDisplay, onboardingChipClass } from '../utils/onboarding';
import OnboardingKitDownloads from '../components/locations/OnboardingKitDownloads';
import './LocationDashboardPage.css';

type TabId = 'inventory' | 'devices' | 'digests' | 'quotes' | 'orders';

interface Tab {
  id: TabId;
  label: string;
  icon: string;
}

const TAB_DEFS: Tab[] = [
  { id: 'inventory', label: 'Inventory', icon: 'inventory_2' },
  { id: 'devices', label: 'Devices', icon: 'router' },
  { id: 'digests', label: 'Digests', icon: 'email' },
  { id: 'orders', label: 'Orders', icon: 'receipt_long' },
  // Hidden until the corresponding flow is finalized. Endpoint, render branch,
  // and query remain in place; uncomment to re-enable.
  // { id: 'quotes', label: 'Open Quotes', icon: 'request_quote' },
];

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Read-only boolean indicator for the Location Configuration. Pairs a check
// or X icon with "On" / "Off" text. Use this instead of a `Switch`-shaped
// element on non-editing displays where interactivity would be implied
// falsely (the actual toggle lives in the inline editor pane).
const ConfigBool: React.FC<{ value: boolean | null | undefined }> = ({ value }) => (
  <span className={`config-bool ${value ? 'on' : 'off'}`}>
    <span className="material-icons">{value ? 'check_circle' : 'cancel'}</span>
    {value ? 'On' : 'Off'}
  </span>
);

// HH:MM (24-hour) → "7:00 AM" for display in the header chip. Blank/invalid
// falls back to the system default of "7:00 AM" so the chip reflects what the
// scheduler will actually use.
const formatDigestSendTime = (hhmm?: string | null) => {
  const raw = (hhmm || '07:00').trim();
  const m = raw.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!m) return raw;
  const hours = Number(m[1]);
  const period = hours >= 12 ? 'PM' : 'AM';
  const display = hours % 12 === 0 ? 12 : hours % 12;
  return `${display}:${m[2]} ${period}`;
};

// Rails friendly-name → IANA zone for short-abbreviation lookup. We only need
// the zones our backend assigns. `UTC` maps to Central because the
// `Location#compute_scheduled_send_at` UTC override fires the job in
// `Central Time (US & Canada)`, so the chip should advertise that.
const RAILS_TZ_TO_IANA: Record<string, string> = {
  'UTC': 'America/Chicago',
  'Central Time (US & Canada)': 'America/Chicago',
  'Eastern Time (US & Canada)': 'America/New_York',
  'Mountain Time (US & Canada)': 'America/Denver',
  'Pacific Time (US & Canada)': 'America/Los_Angeles',
  'Arizona': 'America/Phoenix',
  'Alaska': 'America/Juneau',
  'Hawaii': 'Pacific/Honolulu',
};

// "Central Time (US & Canada)" → "CST" or "CDT" depending on whether the
// current moment is in DST. Falls back to '' (caller drops the trailing
// space) for an unrecognized zone rather than printing a confusing label.
const formatTimezoneAbbrev = (timezone?: string | null) => {
  if (!timezone) return '';
  const iana = RAILS_TZ_TO_IANA[timezone] || timezone;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: iana,
      timeZoneName: 'short',
    }).formatToParts(new Date());
    return parts.find(p => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
};

// Header chip label for Auto-Send. Pluralized day when a digest_restock_day_of_week
// is set ("Auto-Send Mondays at 7:00 AM CST"); falls back to time-only when not
// ("Auto-Send at 7:00 AM CST"). Returns "Auto-Send Off" when disabled.
// An every-N-weeks cadence (sc-6046) is surfaced inline — a restock-only
// location only produces (and auto-sends) a digest on on-cycle weeks, so a
// bare "Mondays" would overstate the frequency.
const formatAutoSendChipLabel = (location: {
  auto_send_enabled?: boolean;
  digest_send_time?: string | null;
  digest_restock_day_of_week?: number | null;
  restock_interval_weeks?: number | null;
  timezone?: string;
}) => {
  if (!location.auto_send_enabled) return 'Auto-Send Off';

  const cadencePart =
    (location.restock_interval_weeks ?? 1) > 1
      ? ` (every ${location.restock_interval_weeks} weeks)`
      : '';
  const dayPart =
    location.digest_restock_day_of_week != null
      ? `${DAY_NAMES[location.digest_restock_day_of_week]}s${cadencePart} `
      : '';
  const time = formatDigestSendTime(location.digest_send_time);
  const tz = formatTimezoneAbbrev(location.timezone);
  return `Auto-Send ${dayPart}at ${time}${tz ? ` ${tz}` : ''}`;
};

// Onboarding lifecycle chip (Outcomes registry, pushed by the Edge Onboard app).
// Sourced from the shared onboarding util so the detail header and the locations
// list never drift. Returns null when there's no onboarding signal — the detail
// header omits the chip in that case (the locations list shows an explicit "N/A").
const onboardingChip = (state: LocationOnboarding['state']) => {
  if (!state) return null;
  const d = onboardingDisplay(state);
  return { cls: onboardingChipClass(d.tone), icon: d.icon, label: d.label };
};

const UNCATEGORIZED = '__uncategorized__';
const EMPTY_PE_GROUPS: InventoryPeGroup[] = [];

// Boxed group (sc-5986): any member with units_per_box. max_count is
// unit-denominated, so boxed groups compare/display total_units; the ONE
// definition for both the PE cards and the drill-down table.
const isBoxedGroup = (g: InventoryPeGroup) => g.members.some(m => m.units_per_box != null);
const EMPTY_UNGROUPED: InventoryUngroupedItem[] = [];
const EMPTY_WAREHOUSE_ROWS: WarehouseInventoryRow[] = [];

const fillClass = (s: InventoryStatus) => (s === 'oos' ? 'low' : s === 'restock' ? 'mid' : 'full');
const discLabelClass = (s: InventoryStatus) => (s === 'oos' ? 'crit' : s === 'restock' ? 'warn' : 'ok');
const statusChip = (s: InventoryStatus) => {
  if (s === 'oos') return { cls: 'chip-danger', dot: 'red', label: 'Out of Stock' };
  if (s === 'restock') return { cls: 'chip-warning', dot: 'amber', label: 'Needs Restock' };
  return { cls: 'chip-success', dot: 'green', label: 'Healthy' };
};

const statusSectionMeta = (s: InventoryStatus) => {
  if (s === 'oos') return { label: 'Out of Stock', dot: 'red' };
  if (s === 'restock') return { label: 'Needs Restock', dot: 'amber' };
  return { label: 'Healthy', dot: 'green' };
};

const freshnessChip = (f: DeviceFreshness) => {
  switch (f) {
    case 'fresh':   return { cls: 'chip-success', dot: 'green', label: 'Fresh' };
    case 'stale':   return { cls: 'chip-warning', dot: 'amber', label: 'Stale' };
    case 'offline': return { cls: 'chip-danger',  dot: 'red',   label: 'Offline' };
    case 'never':   return { cls: 'chip-default', dot: 'red',   label: 'Never reported' };
  }
};

// Compact age formatter: "12s" / "4m" / "2h" / "3d".
const formatAge = (seconds: number | null | undefined) => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h ago`;
};

const renderPeCard = (
  g: InventoryPeGroup,
  expanded: Set<string>,
  toggleGroup: (gid: string) => void,
  fulfillment: LocationOrderFulfillmentResponse | undefined,
  fulfillmentLoading: boolean
) => {
  const key = `pe-${g.pe_id}`;
  const isExpanded = expanded.has(key);
  const max = g.max_count ?? 0;
  // Boxed groups (sc-5986): max_count is denominated in units, so the
  // group-level figure and fill % use total_units. Member rows stay boxes.
  const boxed = isBoxedGroup(g);
  const onHand = boxed ? g.total_units : g.total_on_hand;
  const pct = max > 0 ? Math.min(100, Math.round((onHand / max) * 100)) : 0;
  const chip = statusChip(g.status);
  return (
    <div key={key} className={`pe-card pe-${g.status}`}>
      <div className="pe-card-header" onClick={() => toggleGroup(key)}>
        <div className="pe-card-title-row">
          <span className="pe-card-title">{g.name}</span>
          <span className={`chip ${chip.cls}`} style={{ fontSize: 10 }}>
            <span className={`status-dot ${chip.dot}`}></span>{chip.label}
          </span>
          <span className={`material-icons pe-card-expand ${isExpanded ? 'open' : ''}`}>chevron_right</span>
        </div>
        <div className="pe-card-stats">
          <span className="pe-card-count">
            <strong>{boxed ? onHand.toLocaleString() : onHand}</strong>
            {max > 0
              ? <> of <strong>{boxed ? max.toLocaleString() : max}</strong>{boxed && ' units'}</>
              : <>{boxed && ' units'} on hand</>}
          </span>
          {max > 0 && (
            <>
              <div className="pe-card-bar">
                <div className={`pe-card-bar-fill ${fillClass(g.status)}`} style={{ width: `${pct}%` }}></div>
              </div>
              <span className={`disc-label ${discLabelClass(g.status)}`} style={{ fontSize: 11 }}>{pct}%</span>
            </>
          )}
        </div>
      </div>
      <div className={`pe-card-body ${isExpanded ? '' : 'collapsed'}`}>
        {g.members.map(m => {
          const isOos = m.count === 0;
          const skuKey = `sku:${m.sku}`;
          const skuOpen = expanded.has(skuKey);
          return (
            <React.Fragment key={m.sku}>
              <div className={`pe-sku-row ${isOos ? 'sku-oos' : ''}`} onClick={() => toggleGroup(skuKey)} style={{ cursor: 'pointer' }}>
                <span className="pe-sku-name">{m.sku}</span>
                <span className={`pe-sku-count ${isOos ? 'oos' : ''}`}>{m.count}</span>
                {isOos && (
                  <span className="pe-sku-out">
                    <span className="chip chip-danger" style={{ fontSize: 9, padding: '1px 6px' }}>OUT</span>
                  </span>
                )}
                <span className={`material-icons pe-card-expand ${skuOpen ? 'open' : ''}`}
                      style={{ fontSize: 18, marginLeft: 8, color: 'var(--text-tertiary)' }} title="Order detail">chevron_right</span>
              </div>
              {skuOpen && <SkuOrderDetail sku={m.sku} data={fulfillment} loading={fulfillmentLoading} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const quoteStatusChip = (status: OpenQuote['status']) => {
  if (status === 'draft')     return { cls: 'chip-default', label: 'Draft' };
  return { cls: 'chip-info', label: 'Validated' };
};

const OpenQuoteRow: React.FC<{
  quote: OpenQuote;
  expanded: boolean;
  onToggle: () => void;
}> = ({ quote, expanded, onToggle }) => {
  const chip = quoteStatusChip(quote.status);
  const fmtDate = (s: string | null) => {
    if (!s) return '—';
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, m, d] = s.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString();
    }
    return new Date(s).toLocaleDateString();
  };

  const idCell = quote.hexahq_quote_url ? (
    <a
      href={quote.hexahq_quote_url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}
      title={`Open quote ${quote.hexahq_quote_id} in HexaHQ`}
      onClick={e => e.stopPropagation()}
    >
      #{quote.hexahq_quote_id}
      <span className="material-icons-outlined" style={{ fontSize: 12, verticalAlign: 'middle', marginLeft: 2 }}>open_in_new</span>
    </a>
  ) : (
    <span>#{quote.id}</span>
  );

  const hasLines = quote.lines.length > 0;
  const fmtPrice = (cents: number | null) =>
    cents == null ? '—' : `$${(cents / 100).toFixed(2)}`;

  return (
    <>
      <tr onClick={onToggle} style={{ cursor: hasLines ? 'pointer' : 'default' }}>
        <td style={{ textAlign: 'center' }}>
          {hasLines && (
            <span
              className="material-icons"
              style={{
                color: 'var(--text-tertiary)',
                fontSize: 18,
                transition: 'transform 0.15s',
                transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                verticalAlign: 'middle',
              }}
            >
              chevron_right
            </span>
          )}
        </td>
        <td className="col-number" style={{ textAlign: 'left' }}>{idCell}</td>
        <td><span className={`chip ${chip.cls}`} style={{ fontSize: 10 }}>{chip.label}</span></td>
        <td style={{ fontSize: 11.5 }}>
          {quote.source}
          {quote.source_reference && (
            <span className="text-muted" style={{ fontSize: 10, marginLeft: 4 }}>({quote.source_reference})</span>
          )}
        </td>
        <td className="col-number">{quote.line_count}</td>
        <td style={{ fontSize: 11.5 }}>{fmtDate(quote.requested_delivery_date)}</td>
        <td style={{ fontSize: 11.5 }}>{fmtDate(quote.created_at)}</td>
      </tr>
      {expanded && hasLines && (
        <tr className="quote-expanded-row">
          <td></td>
          <td colSpan={6}>
            <table className="data-table data-table-nested">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Description</th>
                  <th style={{ textAlign: 'right' }}>Quantity</th>
                  <th style={{ textAlign: 'right' }}>Unit Price</th>
                </tr>
              </thead>
              <tbody>
                {quote.lines.map((l, i) => (
                  <tr key={`${quote.id}-${l.sku}-${i}`}>
                    <td className="col-sku">{l.sku}</td>
                    <td style={{ fontSize: 12 }}>{l.description || <span className="text-muted">—</span>}</td>
                    <td className="col-number">{l.quantity}</td>
                    <td className="col-number">{fmtPrice(l.unit_price_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
};

const digestStatusChip = (status: EdgeAccountDigest['status']) => {
  switch (status) {
    case 'draft':        return { cls: 'chip-default', label: 'Draft' };
    case 'approved':     return { cls: 'chip-info',    label: 'Approved' };
    case 'pending_send': return { cls: 'chip-info',    label: 'Pending Send' };
    case 'sent':         return { cls: 'chip-success', label: 'Sent' };
    case 'cancelled':    return { cls: 'chip-warning', label: 'Cancelled' };
  }
};

const formatDigestDate = (d: string | undefined) => {
  if (!d) return '—';
  // digest_date is a YYYY-MM-DD string — parse parts to avoid TZ shift.
  const [y, m, day] = d.split('-').map(Number);
  if (!y || !m || !day) return d;
  return new Date(y, m - 1, day).toLocaleDateString();
};

const formatTs = (ts: string | undefined) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString();
};

const DigestRow: React.FC<{ digest: EdgeAccountDigest; onPreview: () => void }> = ({ digest, onPreview }) => {
  const chip = digestStatusChip(digest.status);
  const sections = digest.sections_included && digest.sections_included.length > 0
    ? digest.sections_included
    : [];
  return (
    <tr style={digest.archived ? { opacity: 0.6 } : undefined}>
      <td>{formatDigestDate(digest.digest_date)}</td>
      <td>
        <span className={`chip ${chip.cls}`} style={{ fontSize: 10 }}>{chip.label}</span>
        {digest.archived && (
          <span className="chip chip-default" style={{ fontSize: 10, marginLeft: 4 }}>Archived</span>
        )}
      </td>
      <td style={{ fontSize: 12 }}>
        {sections.length > 0 ? sections.join(', ') : <span className="text-muted">None</span>}
      </td>
      <td style={{ fontSize: 12 }}>{digest.recipient_email || <span className="text-muted">—</span>}</td>
      <td style={{ fontSize: 11.5 }}>{formatTs(digest.approved_at)}</td>
      <td style={{ fontSize: 11.5 }}>{formatTs(digest.sent_at)}</td>
      <td style={{ fontSize: 12 }}>
        {digest.email_opened
          ? <span title={`Opened ${digest.email_open_count} time${digest.email_open_count === 1 ? '' : 's'}`}>✓ {digest.email_open_count}</span>
          : <span className="text-muted">—</span>}
      </td>
      <td className="col-actions">
        <button className="btn btn-outlined" style={{ padding: '3px 10px', fontSize: 11 }} onClick={onPreview}>
          <span className="material-icons-outlined" style={{ fontSize: 14 }}>visibility</span> Preview
        </button>
      </td>
    </tr>
  );
};

type KpiDetailKind = 'skus' | 'beacons' | 'restock' | 'oos' | 'hva' | 'emails';

interface KpiDetailModalProps {
  open: boolean;
  onClose: () => void;
  kind: null | KpiDetailKind;
  assignments: LocationAssignment[];
  activeBeacons: BeaconAssignment[];
  peGroups: InventoryPeGroup[];
  ungrouped: InventoryUngroupedItem[];
  skuToPe: Map<string, { peName: string; category: string | null }>;
  hvaData?: LocationHvaRateResponse | null;
  emailData?: LocationEmailOpensResponse | null;
}

// Inline editor rendered when the Location Configuration section is in edit mode.
interface ConfigEditorProps {
  draft: Partial<Location>;
  slug: string;
  updateDraft: (patch: Partial<Location>) => void;
  organizations: Array<{ id: number; name: string }>;
  hexahq: ReturnType<typeof useHexahqAutocomplete<HexahqAccount>>;
}

const ConfigEditor: React.FC<ConfigEditorProps> = ({
  draft, slug, updateDraft, organizations, hexahq,
}) => {
  const orgValue = organizations.find(o => o.id === draft.organization_id) ?? null;

  return (
    <div className="config-grid">
      {/* Basic Info */}
      <div className="config-group">
        <div className="config-group-label">Basic Info</div>

        {organizations.length === 0 ? (
          <TextField size="small" label="Organization" value="Loading…" disabled />
        ) : (
          <Autocomplete
            size="small"
            options={organizations}
            value={orgValue ?? organizations[0]}
            getOptionLabel={(o) => o?.name || ''}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_e, v) => updateDraft({ organization_id: v.id })}
            renderInput={(params) => <TextField {...params} label="Organization" />}
            disableClearable
          />
        )}

        <div className="config-item">
          <span className="config-label">Slug</span>
          <span className="config-value text-mono" style={{ fontSize: 11.5 }}>{slug}</span>
        </div>

        <TextField
          size="small"
          label="Timezone"
          value={draft.timezone || ''}
          onChange={e => updateDraft({ timezone: e.target.value })}
          helperText="IANA zone (e.g. America/Chicago)"
        />

        <Autocomplete
          size="small"
          options={hexahq.options}
          filterOptions={(x) => x}
          getOptionLabel={hexahq.formatLabel}
          value={hexahq.selectedOption}
          onChange={(_e, v) => {
            hexahq.setSelectedOption(v);
            updateDraft({ hexahq_account_id: v?.hexahq_id ?? null });
          }}
          inputValue={hexahq.inputValue}
          onInputChange={(e, input, reason) => {
            hexahq.handleInputChange(e, input, reason);
            if (reason === 'clear') updateDraft({ hexahq_account_id: null });
          }}
          isOptionEqualToValue={hexahq.isOptionEqualToValue}
          loading={hexahq.isLoading}
          noOptionsText={hexahq.noOptionsText}
          renderInput={(params) => <TextField {...params} label="HexaHQ Account" placeholder="Search by ID or name" />}
        />
      </div>

      {/* Status & Monitoring */}
      <div className="config-group">
        <div className="config-group-label">Status &amp; Monitoring</div>
        <StatusMonitoringFields
          value={draft}
          onChange={patch => updateDraft(patch)}
          density="compact"
        />
      </div>

      {/* Digest Settings */}
      <div className="config-group">
        <div className="config-group-label">Digest Settings</div>
        <DigestRecipientFields
          value={draft}
          onChange={patch => updateDraft(patch)}
          density="compact"
        />
        <DigestPreferencesFields
          value={draft.digest_preferences}
          onChange={patch => updateDraft(patch)}
          density="compact"
        />
      </div>

      {/* Fulfillment */}
      <div className="config-group">
        <div className="config-group-label">Fulfillment</div>
        <FulfillmentFields
          value={draft}
          onChange={patch => updateDraft(patch)}
          density="compact"
        />
      </div>

      {/* Account Manager */}
      <div className="config-group">
        <div className="config-group-label">Account Manager (Email Footer)</div>
        <AccountManagerFields
          value={draft.digest_preferences}
          onChange={patch => updateDraft(patch)}
          density="compact"
        />
      </div>
    </div>
  );
};

const KpiDetailModal: React.FC<KpiDetailModalProps> = ({
  open, onClose, kind, assignments, activeBeacons, peGroups, ungrouped, skuToPe, hvaData, emailData,
}) => {
  const title = (() => {
    switch (kind) {
      case 'skus':    return 'SKUs Assigned';
      case 'beacons': return 'Active Beacons';
      case 'restock': return 'Needs Restock';
      case 'oos':     return 'Out of Stock';
      case 'hva':     return 'HVA Conversions';
      case 'emails':  return 'Emails Opened';
      default:        return '';
    }
  })();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="span">{title}</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {kind === 'skus' && <SkusTable assignments={assignments} skuToPe={skuToPe} />}
        {kind === 'beacons' && <BeaconsTable beacons={activeBeacons} />}
        {kind === 'restock' && <InventoryStatusTable peGroups={peGroups} ungrouped={ungrouped} status="restock" />}
        {kind === 'oos' && <InventoryStatusTable peGroups={peGroups} ungrouped={ungrouped} status="oos" />}
        {kind === 'hva' && <HvaConversionsTable conversions={hvaData?.conversions ?? []} />}
        {kind === 'emails' && emailData && <EmailCombinationsTable data={emailData} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

type SortDir = 'asc' | 'desc';

// Hook: simple two-state sort (field + direction). Clicking the same field flips direction.
function useTableSort<F extends string>(initialField: F, initialDir: SortDir = 'asc') {
  const [sortField, setSortField] = React.useState<F>(initialField);
  const [sortDir, setSortDir] = React.useState<SortDir>(initialDir);
  const onSort = (field: F) => {
    if (field === sortField) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };
  return { sortField, sortDir, onSort };
}

// Renders a sortable header cell. Extracted so callers just pass field/label.
const renderSortHeader = <F extends string>(
  sortField: F,
  sortDir: SortDir,
  onSort: (f: F) => void,
  field: F,
  label: string,
  align: 'left' | 'right' = 'left'
) => (
  <TableCell align={align} sortDirection={sortField === field ? sortDir : false}>
    <TableSortLabel
      active={sortField === field}
      direction={sortField === field ? sortDir : 'asc'}
      onClick={() => onSort(field)}
    >
      {label}
    </TableSortLabel>
  </TableCell>
);

// Comparator helpers — all respect `dir` and always sort nulls last.
const cmpStr = (a: string, b: string, dir: SortDir) =>
  a.localeCompare(b) * (dir === 'asc' ? 1 : -1);

const cmpNullableStr = (a: string | null | undefined, b: string | null | undefined, dir: SortDir) => {
  const aNull = !a, bNull = !b;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  return cmpStr(a!, b!, dir);
};

const cmpNullableNum = (a: number | null | undefined, b: number | null | undefined, dir: SortDir) => {
  const aNull = a == null, bNull = b == null;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  return (a! - b!) * (dir === 'asc' ? 1 : -1);
};

type SkuSortField = 'sku' | 'pe';

// No Max column here: assignments carry no max — the group max is LPE-grain
// (sc-5845) and shown in the inventory rollup above.
const SkusTable: React.FC<{
  assignments: LocationAssignment[];
  skuToPe: Map<string, { peName: string; category: string | null }>;
}> = ({ assignments, skuToPe }) => {
  const { sortField, sortDir, onSort } = useTableSort<SkuSortField>('sku');

  const sorted = React.useMemo(() => {
    return [...assignments].sort((a, b) => {
      if (sortField === 'sku') return cmpStr(a.sku, b.sku, sortDir);
      return cmpStr(skuToPe.get(a.sku)?.peName ?? '', skuToPe.get(b.sku)?.peName ?? '', sortDir);
    });
  }, [assignments, skuToPe, sortField, sortDir]);

  if (assignments.length === 0) {
    return <Typography variant="body2" color="text.secondary">No active assignments at this location.</Typography>;
  }

  return (
    <TableContainer component={Paper} elevation={0}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {renderSortHeader(sortField, sortDir, onSort, 'sku', 'SKU')}
            {renderSortHeader(sortField, sortDir, onSort, 'pe', 'Product Equivalency')}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map(a => {
            const pe = skuToPe.get(a.sku);
            return (
              <TableRow key={a.id} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{a.sku}</TableCell>
                <TableCell>{pe?.peName || <em>Ungrouped</em>}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

type BeaconSortField = 'beacon' | 'sku' | 'assigned_at' | 'assigned_by' | 'notes';

const BeaconsTable: React.FC<{ beacons: BeaconAssignment[] }> = ({ beacons }) => {
  const { sortField, sortDir, onSort } = useTableSort<BeaconSortField>('sku');

  const sorted = React.useMemo(() => {
    return [...beacons].sort((a, b) => {
      switch (sortField) {
        case 'beacon':      return cmpStr(a.beacon_id, b.beacon_id, sortDir);
        case 'sku':         return cmpStr(a.sku, b.sku, sortDir) || cmpStr(a.beacon_id, b.beacon_id, 'asc');
        case 'assigned_at': return cmpNullableStr(a.assigned_at, b.assigned_at, sortDir);
        case 'assigned_by': return cmpNullableStr(a.assigned_by, b.assigned_by, sortDir);
        case 'notes':       return cmpNullableStr(a.notes, b.notes, sortDir);
        default:            return 0;
      }
    });
  }, [beacons, sortField, sortDir]);

  if (beacons.length === 0) {
    return <Typography variant="body2" color="text.secondary">No active beacons at this location.</Typography>;
  }

  const fmtDate = (s?: string) => (s ? new Date(s).toLocaleString() : '—');

  return (
    <TableContainer component={Paper} elevation={0}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {renderSortHeader(sortField, sortDir, onSort, 'beacon', 'Beacon (MAC)')}
            {renderSortHeader(sortField, sortDir, onSort, 'sku', 'SKU')}
            {renderSortHeader(sortField, sortDir, onSort, 'assigned_at', 'Assigned At')}
            {renderSortHeader(sortField, sortDir, onSort, 'assigned_by', 'Assigned By')}
            {renderSortHeader(sortField, sortDir, onSort, 'notes', 'Notes')}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map(b => (
            <TableRow key={b.beacon_id} hover>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{b.beacon_id}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{b.sku}</TableCell>
              <TableCell sx={{ fontSize: 12 }}>{fmtDate(b.assigned_at)}</TableCell>
              <TableCell sx={{ fontSize: 12 }}>{b.assigned_by || '—'}</TableCell>
              <TableCell sx={{ fontSize: 12 }}>{b.notes || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// sc-5864 — weekly HVA-conversion sparkline for the header card. Single series,
// bars scaled to the peak bucket; reuses the existing .kpi-sparkline/.spark-bar
// styles. An all-zero window renders as flat baseline ticks.
const HvaSparkline: React.FC<{ buckets: HvaSparkBucket[] }> = ({ buckets }) => {
  const peak = Math.max(1, ...buckets.map(b => b.count));
  return (
    <div className="kpi-sparkline" role="img" aria-label="Weekly HVA conversions">
      {buckets.map(b => {
        const h = b.count > 0 ? Math.max(3, Math.round((b.count / peak) * 24)) : 2;
        return (
          <div
            key={b.week_start}
            className="spark-bar"
            style={{ height: h, background: 'var(--success)' }}
            title={`Week of ${b.week_start}: ${b.count} ${b.count === 1 ? 'conversion' : 'conversions'}`}
          />
        );
      })}
    </div>
  );
};

// sc-5864 — converting quote→order pairs behind the HVA card. Newest-first as
// returned by the endpoint; row count reconciles with hva_count.
const HvaConversionsTable: React.FC<{ conversions: HvaConversion[] }> = ({ conversions }) => {
  if (conversions.length === 0) {
    return <Typography variant="body2" color="text.secondary">No HVA conversions in this window.</Typography>;
  }
  return (
    <TableContainer component={Paper} elevation={0}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Quote</TableCell>
            <TableCell>Quoted</TableCell>
            <TableCell>Order</TableCell>
            <TableCell>Ordered</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {conversions.map(c => (
            <TableRow key={`${c.quote_id}-${c.order_id}`} hover>
              <TableCell sx={{ fontSize: 12 }}><QuoteLink id={c.quote_id} /></TableCell>
              <TableCell sx={{ fontSize: 12 }}>{fmtDateTime(c.quote_at)}</TableCell>
              <TableCell sx={{ fontSize: 12 }}><OrderLink id={c.order_id} /></TableCell>
              <TableCell sx={{ fontSize: 12 }}>{fmtDateTime(c.order_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// sc-5864 — by-combination partition behind the Emails Opened card. Rows sum to
// total opened.
const EmailCombinationsTable: React.FC<{ data: LocationEmailOpensResponse }> = ({ data }) => {
  if (data.opened === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No opened digests in this window ({data.opened} of {data.sent} sent).
      </Typography>
    );
  }
  return (
    <>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {data.opened} of {data.sent} digests opened, by section combination:
      </Typography>
      <TableContainer component={Paper} elevation={0}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Sections</TableCell>
              <TableCell align="right">Opened</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.by_combination.map(c => (
              <TableRow key={c.sections.join('+')} hover>
                <TableCell>{c.label}</TableCell>
                <TableCell align="right">{c.opened}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

type InvSortField = 'name' | 'category' | 'on_hand' | 'max' | 'fill';

// Unified row shape so PE groups and ungrouped items sort together.
interface InvRow {
  key: string;
  name: string;
  nameSuffix?: string; // e.g. SKU shown beside name for ungrouped rows
  category: string | null;
  isUngrouped: boolean;
  /** true when the group's on_hand/max are unit-denominated (boxed group, sc-5986) */
  units?: boolean;
  on_hand: number;
  max: number | null;
  fillPct: number | null;
}

const InventoryStatusTable: React.FC<{
  peGroups: InventoryPeGroup[];
  ungrouped: InventoryUngroupedItem[];
  status: InventoryStatus;
}> = ({ peGroups, ungrouped, status }) => {
  // Default: fill % ascending (worst first) — matches the "why you opened this popup" intent.
  const { sortField, sortDir, onSort } = useTableSort<InvSortField>('fill');

  const rows: InvRow[] = React.useMemo(() => {
    const peRows: InvRow[] = peGroups
      .filter(g => g.status === status)
      .map(g => {
        // Boxed groups (sc-5986): max_count is units — on-hand and fill % use
        // total_units, suffixed so they read correctly next to box-grain rows.
        const boxed = isBoxedGroup(g);
        const onHand = boxed ? g.total_units : g.total_on_hand;
        return {
          key: `pe-${g.pe_id}`,
          name: g.name,
          category: g.category,
          isUngrouped: false,
          units: boxed,
          on_hand: onHand,
          max: g.max_count ?? null,
          fillPct: g.max_count ? Math.round((onHand / g.max_count) * 100) : null,
        };
      });
    const unRows: InvRow[] = ungrouped
      .filter(u => u.status === status)
      .map(u => ({
        key: `u-${u.sku}`,
        name: u.product_name,
        nameSuffix: u.sku,
        category: null,
        isUngrouped: true,
        on_hand: u.count,
        max: u.max_count ?? null,
        fillPct: u.max_count ? Math.round((u.count / u.max_count) * 100) : null,
      }));
    return [...peRows, ...unRows];
  }, [peGroups, ungrouped, status]);

  const sorted = React.useMemo(() => {
    return [...rows].sort((a, b) => {
      switch (sortField) {
        case 'name':     return cmpStr(a.name, b.name, sortDir);
        case 'category': return cmpNullableStr(a.category, b.category, sortDir);
        case 'on_hand':  return (a.on_hand - b.on_hand) * (sortDir === 'asc' ? 1 : -1);
        case 'max':      return cmpNullableNum(a.max, b.max, sortDir);
        case 'fill':     return cmpNullableNum(a.fillPct, b.fillPct, sortDir);
        default:         return 0;
      }
    });
  }, [rows, sortField, sortDir]);

  if (rows.length === 0) {
    const msg = status === 'oos' ? 'Nothing is out of stock. Nice.' : 'All groups are stocked above their max.';
    return <Typography variant="body2" color="text.secondary">{msg}</Typography>;
  }

  return (
    <TableContainer component={Paper} elevation={0}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {renderSortHeader(sortField, sortDir, onSort, 'name', 'Name')}
            {renderSortHeader(sortField, sortDir, onSort, 'category', 'Category')}
            {renderSortHeader(sortField, sortDir, onSort, 'on_hand', 'On Hand', 'right')}
            {renderSortHeader(sortField, sortDir, onSort, 'max', 'Max', 'right')}
            {renderSortHeader(sortField, sortDir, onSort, 'fill', 'Fill', 'right')}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map(r => (
            <TableRow key={r.key} hover>
              <TableCell>
                {r.name}
                {r.nameSuffix && (
                  <span style={{ color: '#8a8a85', fontFamily: 'monospace', fontSize: 11, marginLeft: 6 }}>
                    ({r.nameSuffix})
                  </span>
                )}
              </TableCell>
              <TableCell>{r.isUngrouped ? <em>Ungrouped</em> : (r.category || <em>Uncategorized</em>)}</TableCell>
              <TableCell align="right">{r.units ? `${r.on_hand.toLocaleString()} units` : r.on_hand}</TableCell>
              <TableCell align="right">{r.max != null ? (r.units ? r.max.toLocaleString() : r.max) : '—'}</TableCell>
              <TableCell align="right">{r.fillPct != null ? `${r.fillPct}%` : '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const renderDeviceCard = (d: LocationDevice) => {
  const chip = freshnessChip(d.freshness);
  const diag = d.latest_diagnostic;
  const charge = diag?.charge_percentage;
  const chargeLow = charge != null && charge < 20;
  return (
    <div key={d.id} className={`device-card freshness-${d.freshness}`}>
      <div className="device-card-header">
        <div className="device-card-title-row">
          <RouterLink to={`/devices/${d.id}`} className="device-card-id">{d.device_id}</RouterLink>
          <span className={`chip ${chip.cls}`} style={{ fontSize: 10 }}>
            <span className={`status-dot ${chip.dot}`}></span>{chip.label}
          </span>
        </div>
        {d.label && <div className="device-card-label">{d.label}</div>}
        <div className="device-card-meta">
          <span className="loc-meta-item">
            <span className="material-icons-outlined">{d.power_configuration === 'Solar' ? 'solar_power' : 'power'}</span>
            {d.power_configuration}
          </span>
          <span className="loc-meta-item">
            <span className="material-icons-outlined">schedule</span>
            Last seen: <strong style={{ marginLeft: 4 }}>{formatAge(diag?.seconds_ago)}</strong>
          </span>
        </div>
      </div>
      {diag ? (
        <div className="device-card-diag">
          <div className="diag-stat">
            <span className="diag-label">Battery</span>
            <span className={`diag-value ${chargeLow ? 'diag-low' : ''}`}>{charge}%</span>
            <span className="diag-sub">{diag.battery_state}</span>
          </div>
          <div className="diag-stat">
            <span className="diag-label">Input Voltage</span>
            <span className="diag-value">{diag.vin_volts.toFixed(1)}V</span>
          </div>
          <div className="diag-stat">
            <span className="diag-label">Power Source</span>
            <span className="diag-value diag-value-text">{diag.power_source}</span>
          </div>
        </div>
      ) : (
        <div className="device-card-diag-empty">
          <span className="material-icons-outlined">cloud_off</span>
          No diagnostic data received
        </div>
      )}
    </div>
  );
};

const renderUngroupedCard = (u: InventoryUngroupedItem) => {
  const max = u.max_count ?? 0;
  const pct = max > 0 ? Math.min(100, Math.round((u.count / max) * 100)) : 0;
  const chip = statusChip(u.status);
  return (
    <div key={`u-${u.sku}`} className={`pe-card pe-${u.status}`}>
      <div className="pe-card-header">
        <div className="pe-card-title-row">
          <span className="pe-card-title">{u.product_name}</span>
          <span className="pe-card-category text-mono">{u.sku}</span>
          <span className={`chip ${chip.cls}`} style={{ fontSize: 10 }}>
            <span className={`status-dot ${chip.dot}`}></span>{chip.label}
          </span>
        </div>
        <div className="pe-card-stats">
          <span className="pe-card-count">
            <strong>{u.count}</strong>
            {max > 0 ? <> of <strong>{max}</strong></> : ' on hand'}
            {u.reorder_threshold != null && max === 0 && (
              <> &middot; reorder at <strong>{u.reorder_threshold}</strong></>
            )}
          </span>
          {max > 0 && (
            <>
              <div className="pe-card-bar">
                <div className={`pe-card-bar-fill ${fillClass(u.status)}`} style={{ width: `${pct}%` }}></div>
              </div>
              <span className={`disc-label ${discLabelClass(u.status)}`} style={{ fontSize: 11 }}>{pct}%</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Warehouse inventory view (sc-6110) — internal locations only. One grain
// everywhere: rows are (SKU × assigned location) in BOTH states; the Group-by
// toggle only picks which dimension becomes the section header — no hidden
// aggregation, no second query. Counts only: a warehouse has no LPEs, so no
// max/oos/restock semantics exist anywhere in this view.
// ---------------------------------------------------------------------------

export type WarehouseGroupMode = 'location' | 'sku';

const whCount = (boxes: number, units: number) =>
  `${boxes} ${boxes === 1 ? 'box' : 'boxes'} · ${units} ${units === 1 ? 'unit' : 'units'}`;

const sumBoxes = (rows: WarehouseInventoryRow[]) => rows.reduce((s, r) => s + r.boxes_present, 0);
const sumUnits = (rows: WarehouseInventoryRow[]) => rows.reduce((s, r) => s + r.units_present, 0);

// Numeric-aware SKU ordering: catalog families like PES-1 / PES-5 / PES-50 /
// PES-100 must sort by size, not lexicographically (1, 100, 5, 50).
const compareSkus = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });

// Destinations alphabetical, Unassigned LAST (framed "tagged, not yet
// allocated"); rows within a destination sorted by SKU.
export const warehouseSectionsByLocation = (rows: WarehouseInventoryRow[]) => {
  const map = new Map<number | null, WarehouseInventoryRow[]>();
  for (const r of rows) {
    const bucket = map.get(r.assigned_location_id);
    if (bucket) bucket.push(r);
    else map.set(r.assigned_location_id, [r]);
  }
  return Array.from(map.entries())
    .map(([id, rs]) => ({
      key: id == null ? 'unassigned' : `loc-${id}`,
      name: id == null ? null : rs[0].assigned_location_name,
      rows: [...rs].sort((a, b) => compareSkus(a.sku, b.sku)),
      boxes: sumBoxes(rs),
      units: sumUnits(rs),
    }))
    .sort((a, b) => {
      if (a.name == null) return 1;
      if (b.name == null) return -1;
      return a.name.localeCompare(b.name);
    });
};

// Sections sorted by SKU; rows within by destination name, Unassigned last.
export const warehouseSectionsBySku = (rows: WarehouseInventoryRow[]) => {
  const map = new Map<string, WarehouseInventoryRow[]>();
  for (const r of rows) {
    const bucket = map.get(r.sku);
    if (bucket) bucket.push(r);
    else map.set(r.sku, [r]);
  }
  return Array.from(map.entries())
    .map(([sku, rs]) => ({
      key: sku,
      sku,
      productName: rs[0].product_name,
      peGroup: rs[0].pe_group,
      rows: [...rs].sort((a, b) => {
        if (a.assigned_location_name == null) return 1;
        if (b.assigned_location_name == null) return -1;
        return a.assigned_location_name.localeCompare(b.assigned_location_name);
      }),
      boxes: sumBoxes(rs),
      units: sumUnits(rs),
    }))
    .sort((a, b) => compareSkus(a.sku, b.sku));
};

export const WarehouseInventoryView: React.FC<{
  rows: WarehouseInventoryRow[];
  loading: boolean;
  error: boolean;
  groupMode: WarehouseGroupMode;
  onGroupModeChange: (mode: WarehouseGroupMode) => void;
}> = ({ rows, loading, error, groupMode, onGroupModeChange }) => {
  const byLocation = React.useMemo(() => warehouseSectionsByLocation(rows), [rows]);
  const bySku = React.useMemo(() => warehouseSectionsBySku(rows), [rows]);

  return (
    <div>
      <div className="table-toolbar">
        <div className="table-toolbar-left">
          <div className="segmented">
            <span className="segmented-label">Group by</span>
            <button
              type="button"
              className={`segmented-btn ${groupMode === 'location' ? 'active' : ''}`}
              onClick={() => onGroupModeChange('location')}
            >
              Assigned location
            </button>
            <button
              type="button"
              className={`segmented-btn ${groupMode === 'sku' ? 'active' : ''}`}
              onClick={() => onGroupModeChange('sku')}
            >
              SKU
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="tab-placeholder">
          <CircularProgress size={24} />
          <div style={{ marginTop: 8 }}>Loading inventory…</div>
        </div>
      )}

      {error && (
        <div className="tab-placeholder" style={{ color: 'var(--danger)' }}>
          Failed to load inventory.
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="tab-placeholder">
          No tagged inventory present at this location.
          <br />
          Boxes appear here when their tags are sensed PRESENT by the warehouse gateways.
        </div>
      )}

      {!loading && !error && rows.length > 0 && groupMode === 'location' && (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Product</th>
                <th>PE Group</th>
                <th style={{ textAlign: 'right' }}>Boxes</th>
                <th style={{ textAlign: 'right' }}>Units</th>
              </tr>
            </thead>
            <tbody>
              {byLocation.map(sec => (
                <React.Fragment key={sec.key}>
                  <tr className={`wh-group-head ${sec.name == null ? 'wh-unassigned' : ''}`}>
                    <td colSpan={5}>
                      <span className="wh-group-name">{sec.name ?? 'Unassigned'}</span>
                      <span className="wh-group-meta">
                        {sec.name == null ? 'tagged, not yet allocated' : 'staged'} · {whCount(sec.boxes, sec.units)}
                      </span>
                    </td>
                  </tr>
                  {sec.rows.map(r => (
                    <tr key={`${sec.key}:${r.sku}`}>
                      <td className="col-sku">{r.sku}</td>
                      <td>{r.product_name ?? <span className="text-muted">—</span>}</td>
                      <td>{r.pe_group ?? <span className="text-muted">—</span>}</td>
                      <td className="col-number">{r.boxes_present}</td>
                      <td className="col-number">{r.units_present}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && rows.length > 0 && groupMode === 'sku' && (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Assigned To</th>
                <th style={{ textAlign: 'right' }}>Boxes</th>
                <th style={{ textAlign: 'right' }}>Units</th>
              </tr>
            </thead>
            <tbody>
              {bySku.map(sec => (
                <React.Fragment key={sec.key}>
                  <tr className="wh-group-head">
                    <td colSpan={3}>
                      <span className="wh-group-name">
                        <span className="text-mono">{sec.sku}</span>
                        {sec.productName ? ` — ${sec.productName}` : ''}
                      </span>
                      <span className="wh-group-meta">
                        {[sec.peGroup, whCount(sec.boxes, sec.units)].filter(Boolean).join(' · ')}
                      </span>
                    </td>
                  </tr>
                  {sec.rows.map(r => (
                    <tr key={`${sec.key}:${r.assigned_location_id ?? 'unassigned'}`}>
                      <td>{r.assigned_location_name ?? <span className="text-muted">Unassigned</span>}</td>
                      <td className="col-number">{r.boxes_present}</td>
                      <td className="col-number">{r.units_present}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const formatSections = (prefs?: { restock: boolean; daily_usage: boolean; oos: boolean; rma: boolean }) => {
  if (!prefs) return '—';
  const out: string[] = [];
  if (prefs.restock) out.push('Restock');
  if (prefs.daily_usage) out.push('Daily Usage');
  if (prefs.oos) out.push('OOS');
  if (prefs.rma) out.push('RMA');
  return out.length ? out.join(', ') : 'None';
};

export const LocationDashboardPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const locationId = id ? Number(id) : null;

  const [activeTab, setActiveTab] = React.useState<TabId>('inventory');
  const [configOpen, setConfigOpen] = React.useState(false);
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
  const [groupMode, setGroupMode] = React.useState<'category' | 'status'>('category');
  const [warehouseGroupMode, setWarehouseGroupMode] = React.useState<WarehouseGroupMode>('location');
  const [detailModal, setDetailModal] = React.useState<null | KpiDetailKind>(null);
  const [skuSearch, setSkuSearch] = React.useState('');
  const [previewDigestId, setPreviewDigestId] = React.useState<number | null>(null);
  const [expandedQuotes, setExpandedQuotes] = React.useState<Set<number>>(new Set());
  const [editingConfig, setEditingConfig] = React.useState(false);
  const [configDraft, setConfigDraft] = React.useState<Partial<Location> | null>(null);
  const [snackbar, setSnackbar] = React.useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Location>) => locationsApi.update(locationId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location', locationId] });
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setSnackbar({ message: 'Location updated successfully', severity: 'success' });
      setEditingConfig(false);
      setConfigDraft(null);
    },
    onError: (err) => {
      setSnackbar({ message: extractApiError(err, 'Failed to update location'), severity: 'error' });
    },
  });

  const toggleQuoteExpanded = React.useCallback((id: number) => {
    setExpandedQuotes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const { data: location, isLoading: locationLoading, error: locationError } = useQuery({
    queryKey: ['location', locationId],
    queryFn: () => locationsApi.getOne(locationId!),
    enabled: locationId != null,
  });

  const { data: assignments = [] } = useQuery({
    queryKey: ['location-assignments', locationId],
    queryFn: () => locationAssignmentsApi.getByLocation(locationId!, { status: 'active' }),
    enabled: locationId != null,
  });

  const { data: activeBeacons = [] } = useQuery({
    queryKey: ['active-beacons-at-location', locationId],
    queryFn: () => beaconAssignmentsApi.atLocation(locationId!, { activeOnly: true }),
    enabled: locationId != null,
  });

  // sc-5864 header cards — always visible, so eager. Both degrade to "—" via
  // their own account_linked/available flags rather than throwing.
  const { data: hvaRate } = useQuery({
    queryKey: ['location-hva-rate', locationId],
    queryFn: () => locationsApi.getHvaRate(locationId!),
    enabled: locationId != null,
  });

  const { data: emailOpens } = useQuery({
    queryKey: ['location-email-opens', locationId],
    queryFn: () => locationsApi.getEmailOpens30d(locationId!),
    enabled: locationId != null,
  });

  const [showArchived, setShowArchived] = React.useState(false);
  const { data: digests = [] } = useQuery({
    queryKey: ['digests-at-location', locationId, showArchived],
    queryFn: () => edgeAccountDigestsApi.getAll({ location_id: locationId!, include_archived: showArchived }),
    enabled: locationId != null,
  });

  // Fetched only when the config enters edit mode.
  const { data: organizations = [] } = useQuery({
    queryKey: ['organizations'],
    queryFn: organizationsApi.getAll,
    enabled: editingConfig,
  });

  const hexahq = useHexahqAutocomplete<HexahqAccount>({
    searchFn: hexahqCacheApi.accountSearch,
    queryKey: 'hexahq_account_search',
    formatLabel: (opt) => opt.name ? `${opt.hexahq_id}: ${opt.name}` : String(opt.hexahq_id),
    isOptionEqual: (a, b) => a.hexahq_id === b.hexahq_id,
    enabled: editingConfig,
    noMatchText: 'No matching accounts',
  });

  const { data: inventory, isLoading: inventoryLoading, error: inventoryError } = useQuery({
    queryKey: ['location-inventory', locationId],
    queryFn: () => locationsApi.getInventory(locationId!),
    enabled: locationId != null,
  });

  const { data: deviceDetails = [], isLoading: devicesLoading, error: devicesError } = useQuery({
    queryKey: ['location-devices', locationId],
    queryFn: () => locationsApi.getDevices(locationId!),
    enabled: locationId != null,
  });

  // Order Fulfillment Visibility (sc-5858). Drives the Orders tab and the
  // inventory eye-icon pivot, so it's enabled for either surface.
  // Order fulfillment drives the Orders tab and the inventory SKU-row expansion
  // (lazy: only fetched once a SKU row is expanded on the inventory tab).
  const invSkuExpanded = Array.from(expanded).some(k => k.startsWith('sku:'));

  const {
    data: fulfillmentResp,
    isLoading: fulfillmentLoading,
    error: fulfillmentError,
  } = useQuery({
    queryKey: ['location-order-fulfillment', locationId],
    queryFn: () => locationsApi.getOrderFulfillment(locationId!),
    enabled: locationId != null && (activeTab === 'orders' || invSkuExpanded),
  });

  const {
    data: openQuotes = [],
    isLoading: openQuotesLoading,
    error: openQuotesError,
  } = useQuery({
    queryKey: ['location-open-quotes', locationId],
    queryFn: () => locationsApi.getOpenQuotes(locationId!),
    enabled: locationId != null && activeTab === 'quotes',
  });

  // Report-page engagement KPI (sc-5866). Loads with the overview KPIs; renders
  // "—" when unavailable (non-prod / stale sync) via the `available` flag.
  const { data: reportViews } = useQuery({
    queryKey: ['location-report-views', locationId],
    queryFn: () => locationsApi.getReportViews30d(locationId!),
    enabled: locationId != null,
  });

  // Discriminated inventory payload (sc-6110): internal locations carry
  // warehouse rows at (sku × assigned_location) grain; customer locations the
  // LPA-keyed rollup. Narrow once here — everything below consumes one branch.
  const warehouseInventory = inventory?.internal ? inventory : undefined;
  const customerInventory = inventory && !inventory.internal ? inventory : undefined;

  const peGroupsAll: InventoryPeGroup[] = customerInventory?.pe_groups ?? EMPTY_PE_GROUPS;
  const ungroupedAll: InventoryUngroupedItem[] = customerInventory?.ungrouped ?? EMPTY_UNGROUPED;

  // Apply the SKU search filter. PE groups match if any of their member SKUs
  // contain the query; ungrouped items match on their own SKU. Matched PEs
  // keep all members visible so siblings stay in context.
  const searchQuery = skuSearch.trim().toLowerCase();
  const peGroups = React.useMemo<InventoryPeGroup[]>(() => {
    if (!searchQuery) return peGroupsAll;
    return peGroupsAll.filter(g => g.members.some(m => m.sku.toLowerCase().includes(searchQuery)));
  }, [peGroupsAll, searchQuery]);
  const ungrouped = React.useMemo<InventoryUngroupedItem[]>(() => {
    if (!searchQuery) return ungroupedAll;
    return ungroupedAll.filter(u => u.sku.toLowerCase().includes(searchQuery));
  }, [ungroupedAll, searchQuery]);

  const totalInventoryRows = peGroups.length + ungrouped.length;
  const allExpanded = expanded.size > 0 && expanded.size === totalInventoryRows;

  // Aggregate device freshness for the KPI card.
  // Priority: any offline/never → red; else any stale → amber; else all fresh → green.
  const aggregateFreshness: 'ok' | 'warn' | 'bad' | 'neutral' = React.useMemo(() => {
    if (deviceDetails.length === 0) return 'neutral';
    const hasOffline = deviceDetails.some(d => d.freshness === 'offline' || d.freshness === 'never');
    if (hasOffline) return 'bad';
    const hasStale = deviceDetails.some(d => d.freshness === 'stale');
    if (hasStale) return 'warn';
    return 'ok';
  }, [deviceDetails]);

  // Index device details by id for O(1) lookup from the KPI card render.
  const deviceDetailById = React.useMemo(() => {
    const map = new Map<number, typeof deviceDetails[number]>();
    for (const d of deviceDetails) map.set(d.id, d);
    return map;
  }, [deviceDetails]);

  // Group PEs by category, preserving backend order (sorted by category, then
  // LPE position NULLS LAST, then pe_name — sc-6000). Do NOT re-sort groups
  // client-side here or the per-location configured order is lost.
  const peGroupsByCategory = React.useMemo(() => {
    const map = new Map<string, InventoryPeGroup[]>();
    for (const g of peGroups) {
      const key = g.category ?? UNCATEGORIZED;
      const bucket = map.get(key);
      if (bucket) bucket.push(g);
      else map.set(key, [g]);
    }
    return Array.from(map.entries());
  }, [peGroups]);

  // Build SKU → PE name map from inventory data (authoritative at this location).
  const skuToPe = React.useMemo(() => {
    const map = new Map<string, { peName: string; category: string | null }>();
    for (const g of peGroups) {
      for (const m of g.members) map.set(m.sku, { peName: g.name, category: g.category });
    }
    return map;
  }, [peGroups]);

  // Group PEs + ungrouped SKUs by status for the alternate view.
  // Order: oos first (most urgent), then restock, then healthy.
  const inventoryByStatus = React.useMemo(() => {
    const buckets: Record<InventoryStatus, { pe: InventoryPeGroup[]; ungrouped: InventoryUngroupedItem[] }> = {
      oos: { pe: [], ungrouped: [] },
      restock: { pe: [], ungrouped: [] },
      healthy: { pe: [], ungrouped: [] },
    };
    for (const g of peGroups) buckets[g.status].pe.push(g);
    for (const u of ungrouped) buckets[u.status].ungrouped.push(u);
    return buckets;
  }, [peGroups, ungrouped]);

  const startEditingConfig = () => {
    if (!location) return;
    const prefs: Partial<DigestPreferences> = location.digest_preferences || {};
    setConfigDraft({
      organization_id: location.organization_id,
      timezone: location.timezone,
      hexahq_account_id: location.hexahq_account_id ?? null,
      active: location.active,
      alerts_enabled: location.alerts_enabled,
      auto_send_enabled: location.auto_send_enabled ?? false,
      digest_send_time: location.digest_send_time ?? '',
      digest_recipient_email: location.digest_recipient_email ?? '',
      digest_recipient_name: location.digest_recipient_name ?? '',
      digest_restock_day_of_week: location.digest_restock_day_of_week ?? null,
      restock_interval_weeks: location.restock_interval_weeks ?? 1,
      restock_anchor_date: location.restock_anchor_date ?? null,
      quote_confirmation_deadline: location.quote_confirmation_deadline ?? '',
      collect_account_info: location.collect_account_info ?? '',
      uses_stock_truck: location.uses_stock_truck ?? false,
      stock_truck_delivery_day: location.stock_truck_delivery_day ?? null,
      ground_transit_business_days: location.ground_transit_business_days ?? null,
      freight_option: location.freight_option ?? '',
      stock_truck_route: location.stock_truck_route ?? '',
      digest_preferences: {
        restock: prefs.restock ?? true,
        daily_usage: prefs.daily_usage ?? false,
        oos: prefs.oos ?? false,
        rma: prefs.rma ?? false,
        account_manager_name: prefs.account_manager_name ?? '',
        account_manager_email: prefs.account_manager_email ?? '',
        account_manager_title: prefs.account_manager_title ?? '',
        account_manager_phone: prefs.account_manager_phone ?? '',
      },
    });
    // Prime the HexaHQ autocomplete with the current selection.
    if (location.hexahq_account_id) {
      hexahq.setSelectedOption({ hexahq_id: location.hexahq_account_id, name: location.hexahq_account_name || '' });
      hexahq.setInputValue(String(location.hexahq_account_id));
    } else {
      hexahq.setSelectedOption(null);
      hexahq.setInputValue('');
    }
    setEditingConfig(true);
  };

  const cancelEditingConfig = () => {
    setEditingConfig(false);
    setConfigDraft(null);
    updateMutation.reset();
  };

  const saveConfig = () => {
    if (!configDraft) return;
    // Same submit gate as LocationForm — the offending cadence field already
    // shows its inline error inside DigestRecipientFields.
    if (Object.keys(restockCadenceErrors(configDraft)).length > 0) return;
    updateMutation.mutate(configDraft);
  };

  const updateDraft = (patch: Partial<Location>) =>
    setConfigDraft(prev => (prev ? { ...prev, ...patch } : prev));

  const toggleGroup = (gid: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid); else next.add(gid);
      return next;
    });
  };
  const toggleCollapseAll = () => {
    if (allExpanded) {
      setExpanded(new Set());
    } else {
      const allIds = [
        ...peGroups.map(g => `pe-${g.pe_id}`),
        ...ungrouped.map(u => `u-${u.sku}`),
      ];
      setExpanded(new Set(allIds));
    }
  };

  if (locationId == null || Number.isNaN(locationId)) {
    return <Alert severity="error">Invalid location ID.</Alert>;
  }
  if (locationLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (locationError || !location) {
    return <Alert severity="error">Failed to load location.</Alert>;
  }

  const prefs = location.digest_preferences;
  const devices = location.devices || [];
  const activeBeaconCount = activeBeacons.length;

  const tabCounts: Record<TabId, number | null> = {
    // Internal locations: active assignments point AT destinations, not at the
    // warehouse, so the customer count would read 0 next to a full table —
    // show total boxes present instead (null while the payload loads).
    inventory: location.internal
      ? (warehouseInventory ? sumBoxes(warehouseInventory.rows) : null)
      : activeBeaconCount,
    devices: devices.length,
    digests: digests.length,
    quotes: openQuotes.length || null,
    orders: fulfillmentResp?.orders.length ?? null,
  };

  const restockDayName = location.digest_restock_day_of_week != null
    ? DAY_NAMES[location.digest_restock_day_of_week]
    : '—';
  // Surface a non-weekly cadence next to the day so the read-only summary
  // doesn't imply weekly for an every-N-weeks location (sc-6046).
  const restockCadenceSuffix =
    location.digest_restock_day_of_week != null && (location.restock_interval_weeks ?? 1) > 1
      ? ` · every ${location.restock_interval_weeks} weeks`
      : '';

  return (
    <div className="loc-dash">
      {/* Breadcrumb */}
      <nav className="breadcrumb">
        <RouterLink to="/locations">Locations</RouterLink>
        <span className="material-icons breadcrumb-sep">chevron_right</span>
        <span className="breadcrumb-current">{location.name}</span>
      </nav>

      {/* Location Header */}
      <div className="loc-header">
        <div className="loc-header-left">
          <div className="loc-title-row">
            <h1 className="loc-title">{location.name}</h1>
            {location.internal && (
              <span className="chip chip-info" title="HEXA-internal facility (warehouse/tagging site) — no customer, counts-only inventory">
                <span className="material-icons">warehouse</span>
                Internal
              </span>
            )}
            <span className={`chip ${location.active ? 'chip-success' : 'chip-default'}`}>
              <span className="material-icons">{location.active ? 'check_circle' : 'pause_circle'}</span>
              {location.active ? 'Active' : 'Inactive'}
            </span>
            {location.active && (
              <span className={`chip ${location.alerts_enabled ? 'chip-info' : 'chip-default'}`}>
                <span className="material-icons">{location.alerts_enabled ? 'notifications_active' : 'notifications_off'}</span>
                {location.alerts_enabled ? 'Alerts On' : 'Alerts Off'}
              </span>
            )}
            {location.active && (
              <span className={`chip ${location.auto_send_enabled ? 'chip-info' : 'chip-default'}`}>
                <span className="material-icons">schedule_send</span>
                {formatAutoSendChipLabel(location)}
              </span>
            )}
            {(() => {
              const ob = onboardingChip(location.onboarding?.state ?? null);
              return ob ? (
                <span className={`chip ${ob.cls}`} title="Edge onboarding status">
                  <span className="material-icons">{ob.icon}</span>
                  {ob.label}
                </span>
              ) : null;
            })()}
          </div>
          {location.onboarding?.state && location.onboarding.state !== 'not_started' && (
            <div className="loc-meta loc-onboarding-meta">
              <span className="loc-meta-item" title="First login to the Edge Onboard app">
                <span className="material-icons-outlined">login</span>
                First login: {formatTs(location.onboarding.first_login_at ?? undefined)}
              </span>
              <span className="loc-meta-item" title="Onboarding started">
                <span className="material-icons-outlined">play_circle</span>
                Started: {formatTs(location.onboarding.started_at ?? undefined)}
              </span>
              <span className="loc-meta-item" title="Onboarding completed">
                <span className="material-icons-outlined">flag</span>
                Completed: {formatTs(location.onboarding.completed_at ?? undefined)}
              </span>
            </div>
          )}
          <div className="loc-meta">
            {location.organization?.name && (
              <span className="loc-meta-item">
                <span className="material-icons-outlined">business</span>
                {location.organization.name}
              </span>
            )}
            <span className="loc-meta-item">
              <span className="material-icons-outlined">tag</span>
              {location.internal ? (
                // The public report deliberately 404s for internal locations
                // (sc-5719) — same dead-link cleanup as the locations list.
                <span className="text-mono" title="Internal locations have no customer report">{location.slug}</span>
              ) : (
                <a
                  href={
                    ENV.isProduction
                      ? `https://edge.symplirent.com/reports/${location.slug}`
                      : `${API_CONFIG.baseURL}/reports/${location.slug}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="slug-link"
                  title="Open customer dashboard in a new tab"
                >
                  {location.slug}
                  <span
                    className="material-icons-outlined"
                    style={{ fontSize: 14, verticalAlign: 'middle', marginLeft: 3 }}
                  >
                    open_in_new
                  </span>
                </a>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="kpi-grid">
        {/* Devices — colored by aggregate freshness */}
        <div
          className={`kpi-card kpi-devices kpi-devices-${aggregateFreshness} kpi-clickable`}
          onClick={() => setActiveTab('devices')}
          role="button"
          tabIndex={0}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveTab('devices'); } }}
        >
          <div className="kpi-header">
            <span className="kpi-label">Devices</span>
            <div className="kpi-icon"><span className="material-icons-outlined">router</span></div>
          </div>
          {devices.length === 0 ? (
            <div className="kpi-sub" style={{ marginTop: 8 }}>No devices assigned</div>
          ) : (
            <div className="device-list">
              {devices.map(d => {
                const detail = deviceDetailById.get(d.id);
                const chip = detail ? freshnessChip(detail.freshness) : null;
                const age = detail?.latest_diagnostic?.seconds_ago;
                return (
                  <div key={d.id} className="device-list-item">
                    <div>
                      {chip && <span className={`device-status-dot`} style={{ background: `var(--${chip.dot === 'green' ? 'success' : chip.dot === 'amber' ? 'warning' : 'danger'})` }}></span>}
                      <RouterLink
                        to={`/devices/${d.id}`}
                        className="device-id"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {d.device_id}
                      </RouterLink>
                      <span className="device-meta">&nbsp;&middot; {d.power_configuration}</span>
                    </div>
                    <span className="device-meta" style={{ fontSize: 10 }}>
                      {detail ? formatAge(age) : '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Needs Restock / Out of Stock — max-comparator semantics; a warehouse
            has no LPEs, so there is no comparator and the cards would read a
            permanent, misleading 0 (sc-6110). */}
        {!location.internal && (
          <>
            <div
              className="kpi-card kpi-restock kpi-clickable"
              onClick={() => setDetailModal('restock')}
              role="button"
              tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailModal('restock'); } }}
            >
              <div className="kpi-header">
                <span className="kpi-label">Needs Restock</span>
                <div className="kpi-icon"><span className="material-icons-outlined">inventory</span></div>
              </div>
              <div className="kpi-value-row">
                <span className="kpi-value">{customerInventory?.summary.restock_count ?? 0}</span>
              </div>
              <div className="kpi-sub">below max count</div>
            </div>

            <div
              className="kpi-card kpi-oos kpi-clickable"
              onClick={() => setDetailModal('oos')}
              role="button"
              tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailModal('oos'); } }}
            >
              <div className="kpi-header">
                <span className="kpi-label">Out of Stock</span>
                <div className="kpi-icon"><span className="material-icons-outlined">remove_shopping_cart</span></div>
              </div>
              <div className="kpi-value-row">
                <span className="kpi-value">{customerInventory?.summary.oos_count ?? 0}</span>
              </div>
              <div className="kpi-sub">
                {customerInventory && customerInventory.summary.oos_count === 0 ? 'none' : 'groups with 0 on hand'}
              </div>
            </div>
          </>
        )}

        {/* HVA — conversion velocity (sc-5864). Count + per-month rate caption
            + weekly sparkline; click opens the converting quote→order pairs.
            "—" when unlinked or the HQ replica is unavailable. */}
        {(() => {
          const ready = !!hvaRate?.available && !!hvaRate?.account_linked;
          const degradedMsg = !hvaRate ? 'Loading…'
            : !hvaRate.account_linked ? 'Location not linked to an account'
            : !hvaRate.available ? 'HVA data unavailable here'
            : '';
          const sub = ready
            ? `${hvaRate!.rate_per_month != null ? hvaRate!.rate_per_month.toFixed(1) : '—'}/mo · last ${hvaRate!.window_days}d`
            : degradedMsg;
          return (
            <div
              className="kpi-card kpi-hva kpi-clickable"
              onClick={() => { if (ready) setDetailModal('hva'); }}
              role="button"
              tabIndex={0}
              onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && ready) { e.preventDefault(); setDetailModal('hva'); } }}
              title={ready ? undefined : degradedMsg}
            >
              <div className="kpi-header">
                <span className="kpi-label">HVA Conversions</span>
                <div className="kpi-icon"><span className="material-icons-outlined">trending_up</span></div>
              </div>
              <div className="kpi-value-row">
                <span className="kpi-value">{ready ? hvaRate!.hva_count : '—'}</span>
              </div>
              <div className="kpi-sub">{sub}</div>
              {ready && hvaRate!.spark.length > 0 && <HvaSparkline buckets={hvaRate!.spark} />}
            </div>
          );
        })()}

        {/* Emails Opened (sc-5864). opened/sent over the floor-clamped 30d
            window; click opens the by-combination partition. "—" when unlinked
            or open-tracking data is unavailable here. */}
        {(() => {
          const ready = !!emailOpens?.available && !!emailOpens?.account_linked;
          const degradedMsg = !emailOpens ? 'Loading…'
            : !emailOpens.account_linked ? 'Location not linked to an account'
            : !emailOpens.available ? 'Open-tracking unavailable here'
            : '';
          const since = emailOpens?.since
            ? new Date(emailOpens.since).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
            : null;
          const pct = ready && emailOpens!.sent > 0
            ? Math.round((emailOpens!.opened / emailOpens!.sent) * 100)
            : null;
          const sub = ready
            ? (emailOpens!.sent > 0 ? `${pct}% opened · since ${since}` : `none sent · since ${since}`)
            : degradedMsg;
          return (
            <div
              className="kpi-card kpi-emails kpi-clickable"
              onClick={() => { if (ready) setDetailModal('emails'); }}
              role="button"
              tabIndex={0}
              onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && ready) { e.preventDefault(); setDetailModal('emails'); } }}
              title={ready ? undefined : degradedMsg}
            >
              <div className="kpi-header">
                <span className="kpi-label">Emails Opened</span>
                <div className="kpi-icon"><span className="material-icons-outlined">drafts</span></div>
              </div>
              <div className="kpi-value-row">
                <span className="kpi-value">{ready ? `${emailOpens!.opened}/${emailOpens!.sent}` : '—'}</span>
              </div>
              <div className="kpi-sub">{sub}</div>
            </div>
          );
        })()}

        {/* Customer dashboard views (30d) — external report-page engagement (sc-5866) */}
        <div className="kpi-card kpi-report-views">
          <div className="kpi-header">
            <span className="kpi-label">Customer Dashboard Views</span>
            <div className="kpi-icon"><span className="material-icons-outlined">visibility</span></div>
          </div>
          <div className="kpi-value-row">
            <span
              className="kpi-value"
              title="How many times your customer opened their dashboard in the last 30 days."
            >
              {reportViews?.available ? reportViews.pageviews : '—'}
            </span>
          </div>
          {reportViews?.available && (
            <div className="kpi-caption">times opened, last 30 days</div>
          )}
          <div className="kpi-sub">
            {reportViews?.available
              ? <>{reportViews.visitors} different {reportViews.visitors === 1 ? 'person' : 'people'} &middot; {reportViews.sessions} occasion{reportViews.sessions === 1 ? '' : 's'}</>
              : 'no views yet'}
          </div>
        </div>

      </div>

      {/* Config Section */}
      <div className="config-section">
        <div
          className="config-header"
          onClick={() => { if (!editingConfig) setConfigOpen(o => !o); }}
          style={{ cursor: editingConfig ? 'default' : 'pointer' }}
        >
          <div className="config-header-left">
            <span className="material-icons-outlined">settings</span>
            Location Configuration
          </div>
          <div className="config-header-right">
            {editingConfig ? (
              <>
                <button
                  className="btn btn-outlined"
                  style={{ padding: '3px 10px', fontSize: 11 }}
                  onClick={e => { e.stopPropagation(); cancelEditingConfig(); }}
                  disabled={updateMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  style={{ padding: '3px 10px', fontSize: 11 }}
                  onClick={e => { e.stopPropagation(); saveConfig(); }}
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? 'Saving…' : 'Save'}
                </button>
              </>
            ) : (
              <button
                className="btn btn-outlined"
                style={{ padding: '3px 10px', fontSize: 11 }}
                onClick={e => { e.stopPropagation(); setConfigOpen(true); startEditingConfig(); }}
              >
                <span className="material-icons-outlined" style={{ fontSize: 14 }}>edit</span> Edit
              </button>
            )}
            <span
              className="material-icons"
              style={{
                color: 'var(--text-tertiary)',
                fontSize: 20,
                transition: 'transform 0.2s',
                transform: configOpen ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
            >
              chevron_right
            </span>
          </div>
        </div>
        <div className={`config-body ${configOpen ? '' : 'collapsed'}`}>
          {editingConfig && configDraft ? (
            <ConfigEditor
              draft={configDraft}
              slug={location.slug}
              updateDraft={updateDraft}
              organizations={organizations}
              hexahq={hexahq}
            />
          ) : (
            <div className="config-grid">
              <div className="config-group">
                <div className="config-group-label">Basic Info</div>
                <div className="config-item"><span className="config-label">Organization</span><span className="config-value">{location.organization?.name || '—'}</span></div>
                <div className="config-item"><span className="config-label">Slug</span><span className="config-value text-mono" style={{ fontSize: 11.5 }}>{location.slug}</span></div>
                <div className="config-item"><span className="config-label">Timezone</span><span className="config-value">{location.timezone}</span></div>
                <div className="config-item">
                  <span className="config-label">HexaHQ Account</span>
                  <span className={`config-value ${location.hexahq_account_id ? '' : 'muted'}`}>
                    {location.hexahq_account_id
                      ? (location.hexahq_account_name || `#${location.hexahq_account_id}`)
                      : 'Not linked'}
                  </span>
                </div>
              </div>

              <div className="config-group">
                <div className="config-group-label">Status &amp; Monitoring</div>
                <div className="config-item">
                  <span className="config-label">Active</span>
                  <ConfigBool value={location.active} />
                </div>
                <div className="config-item">
                  <span className="config-label">Alerts Enabled</span>
                  <ConfigBool value={location.alerts_enabled} />
                </div>
                <div className="config-item">
                  <span className="config-label">Auto-Send</span>
                  <ConfigBool value={location.auto_send_enabled} />
                </div>
                {location.auto_send_enabled && (
                  <div className="config-item">
                    <span className="config-label">Auto-Send Time</span>
                    <span className="config-value">{formatDigestSendTime(location.digest_send_time)}</span>
                  </div>
                )}
              </div>

              <div className="config-group">
                <div className="config-group-label">Digest Settings</div>
                <div className="config-item">
                  <span className="config-label">Recipient Email</span>
                  <span className={`config-value ${location.digest_recipient_email ? '' : 'muted'}`} style={{ fontSize: 11.5 }}>
                    {location.digest_recipient_email || 'Not set'}
                  </span>
                </div>
                <div className="config-item">
                  <span className="config-label">Recipient Name</span>
                  <span className={`config-value ${location.digest_recipient_name ? '' : 'muted'}`}>
                    {location.digest_recipient_name || 'Not set'}
                  </span>
                </div>
                <div className="config-item">
                  <span className="config-label">Restock Day</span>
                  <span className="config-value">{restockDayName}{restockCadenceSuffix}</span>
                </div>
                <div className="config-item">
                  <span className="config-label">Sections</span>
                  <span className="config-value" style={{ fontSize: 11 }}>{formatSections(prefs)}</span>
                </div>
              </div>

              <div className="config-group">
                <div className="config-group-label">Fulfillment</div>
                <div className="config-item">
                  <span className="config-label">Quote Confirmation Deadline</span>
                  <span className={`config-value ${location.quote_confirmation_deadline ? '' : 'muted'}`}>
                    {location.quote_confirmation_deadline || 'Not set'}
                  </span>
                </div>
                <div className="config-item">
                  <span className="config-label">Collect Account Info</span>
                  <span className={`config-value ${location.collect_account_info ? '' : 'muted'}`} style={{ fontSize: 11.5 }}>
                    {location.collect_account_info || 'Not set'}
                  </span>
                </div>
                <div className="config-item">
                  <span className="config-label">Stock Truck</span>
                  <ConfigBool value={location.uses_stock_truck} />
                </div>
                {location.uses_stock_truck ? (
                  <div className="config-item">
                    <span className="config-label">Stock Truck Delivery Day</span>
                    <span className={`config-value ${location.stock_truck_delivery_day != null ? '' : 'muted'}`}>
                      {location.stock_truck_delivery_day != null ? DAY_NAMES[location.stock_truck_delivery_day] : 'Not set'}
                    </span>
                  </div>
                ) : (
                  <div className="config-item">
                    <span className="config-label">Ground Transit Business Days</span>
                    <span className={`config-value ${location.ground_transit_business_days != null ? '' : 'muted'}`}>
                      {location.ground_transit_business_days != null ? location.ground_transit_business_days : 'Not set'}
                    </span>
                  </div>
                )}
                {location.uses_stock_truck && (
                  <div className="config-item">
                    <span className="config-label">Stock Truck Route</span>
                    <span className={`config-value ${location.stock_truck_route ? '' : 'muted'}`}>
                      {location.stock_truck_route || 'Not set'}
                    </span>
                  </div>
                )}
                <div className="config-item">
                  <span className="config-label">Freight Option</span>
                  <span className={`config-value ${location.freight_option ? '' : 'muted'}`}>
                    {location.freight_option || 'Not set'}
                  </span>
                </div>
              </div>

              <div className="config-group">
                <div className="config-group-label">Account Manager (Email Footer)</div>
                <div className="config-item">
                  <span className="config-label">Name</span>
                  <span className={`config-value ${prefs?.account_manager_name ? '' : 'muted'}`}>
                    {prefs?.account_manager_name || 'Not set'}
                  </span>
                </div>
                <div className="config-item">
                  <span className="config-label">Email</span>
                  <span className={`config-value ${prefs?.account_manager_email ? '' : 'muted'}`} style={{ fontSize: 11.5 }}>
                    {prefs?.account_manager_email || 'Not set'}
                  </span>
                </div>
                <div className="config-item">
                  <span className="config-label">Title</span>
                  <span className={`config-value ${prefs?.account_manager_title ? '' : 'muted'}`}>
                    {prefs?.account_manager_title || 'Not set'}
                  </span>
                </div>
                <div className="config-item">
                  <span className="config-label">Phone</span>
                  <span className={`config-value ${prefs?.account_manager_phone ? '' : 'muted'}`}>
                    {prefs?.account_manager_phone || 'Not set'}
                  </span>
                </div>
              </div>
            </div>
          )}
          {/* The printed onboarding kit (sc-6305). Lives with the config rather
              than the metrics above it: it's a property of how this location is
              set up, and reps need it long after go-live. Gated here as well as
              in the component so an internal location gets no orphan divider. */}
          {!location.internal && (
            <div className="config-kit">
              <div className="config-group-label" style={{ marginBottom: 10 }}>Onboarding Print Kit</div>
              <OnboardingKitDownloads locationId={location.id} />
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs-bar">
          {TAB_DEFS.map(t => {
            const count = tabCounts[t.id];
            return (
              <button
                key={t.id}
                className={`tab ${activeTab === t.id ? 'active' : ''}`}
                onClick={() => setActiveTab(t.id)}
                type="button"
              >
                <span className="material-icons-outlined">{t.icon}</span>
                {t.label}
                {count != null && <span className="tab-count">{count}</span>}
              </button>
            );
          })}
        </div>

        {activeTab === 'inventory' && location.internal && (
          <WarehouseInventoryView
            rows={warehouseInventory?.rows ?? EMPTY_WAREHOUSE_ROWS}
            loading={inventoryLoading}
            error={!!inventoryError}
            groupMode={warehouseGroupMode}
            onGroupModeChange={setWarehouseGroupMode}
          />
        )}

        {activeTab === 'inventory' && !location.internal && (
          <div>
            <div className="table-toolbar">
              <div className="table-toolbar-left">
                <div className="search-box">
                  <span className="material-icons-outlined">search</span>
                  <input
                    type="text"
                    placeholder="Search by SKU…"
                    value={skuSearch}
                    onChange={e => setSkuSearch(e.target.value)}
                  />
                  {skuSearch && (
                    <span
                      className="material-icons-outlined search-clear"
                      onClick={() => setSkuSearch('')}
                      title="Clear search"
                    >
                      close
                    </span>
                  )}
                </div>
                <div className="segmented">
                  <span className="segmented-label">Group by</span>
                  <button
                    type="button"
                    className={`segmented-btn ${groupMode === 'category' ? 'active' : ''}`}
                    onClick={() => setGroupMode('category')}
                  >
                    Category
                  </button>
                  <button
                    type="button"
                    className={`segmented-btn ${groupMode === 'status' ? 'active' : ''}`}
                    onClick={() => setGroupMode('status')}
                  >
                    Status
                  </button>
                </div>
              </div>
              <div className="flex-center gap-8">
                {/* SKUs-assigned detail, re-homed here from the former header card (sc-5864). */}
                <IconButton
                  size="small"
                  onClick={() => setDetailModal('skus')}
                  title={`SKUs assigned (${assignments.length})`}
                  aria-label="View SKUs assigned"
                >
                  <span className="material-icons-outlined" style={{ fontSize: 18 }}>inventory_2</span>
                </IconButton>
                <div
                  className={`toggle-group ${!allExpanded ? 'active' : ''}`}
                  onClick={toggleCollapseAll}
                >
                  <span className="material-icons-outlined">{allExpanded ? 'unfold_less' : 'unfold_more'}</span>
                  {allExpanded ? 'Collapse All' : 'Expand All'}
                </div>
              </div>
            </div>

            {inventoryLoading && (
              <div className="tab-placeholder">
                <CircularProgress size={24} />
                <div style={{ marginTop: 8 }}>Loading inventory…</div>
              </div>
            )}

            {inventoryError && (
              <div className="tab-placeholder" style={{ color: 'var(--danger)' }}>
                Failed to load inventory.
              </div>
            )}

            {!inventoryLoading && !inventoryError && totalInventoryRows === 0 && (
              <div className="tab-placeholder">
                {searchQuery
                  ? <>No SKUs match <strong>"{skuSearch}"</strong>.</>
                  : 'No products assigned to this location yet.'}
              </div>
            )}

            {!inventoryLoading && !inventoryError && totalInventoryRows > 0 && groupMode === 'category' && (
              <div className="category-sections">
                {peGroupsByCategory.map(([cat, groups]) => (
                  <div key={cat} className="category-section">
                    <div className="category-header">
                      <span className="category-title">
                        {cat === UNCATEGORIZED ? 'Uncategorized' : cat}
                      </span>
                      <span className="category-count">{groups.length} product {groups.length === 1 ? 'equivalency' : 'equivalencies'}</span>
                    </div>
                    <div className="pe-card-grid">
                      {groups.map(g => renderPeCard(g, expanded, toggleGroup, fulfillmentResp, fulfillmentLoading))}
                    </div>
                  </div>
                ))}
                {ungrouped.length > 0 && (
                  <div className="category-section">
                    <div className="category-header">
                      <span className="category-title">Ungrouped</span>
                      <span className="category-count">{ungrouped.length} {ungrouped.length === 1 ? 'SKU' : 'SKUs'}</span>
                    </div>
                    <div className="pe-card-grid">
                      {ungrouped.map(renderUngroupedCard)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!inventoryLoading && !inventoryError && totalInventoryRows > 0 && groupMode === 'status' && (
              <div className="category-sections">
                {(['oos', 'restock', 'healthy'] as const).map(status => {
                  const { pe, ungrouped: un } = inventoryByStatus[status];
                  const count = pe.length + un.length;
                  if (count === 0) return null;
                  const meta = statusSectionMeta(status);
                  return (
                    <div key={status} className="category-section">
                      <div className="category-header">
                        <span className={`status-dot ${meta.dot}`} style={{ marginRight: 2 }}></span>
                        <span className="category-title">{meta.label}</span>
                        <span className="category-count">{count} {count === 1 ? 'item' : 'items'}</span>
                      </div>
                      <div className="pe-card-grid">
                        {pe.map(g => renderPeCard(g, expanded, toggleGroup, fulfillmentResp, fulfillmentLoading))}
                        {un.map(renderUngroupedCard)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'devices' && (
          <div>
            {/* Active-beacons detail, re-homed here from the former header card (sc-5864). */}
            <div className="table-toolbar">
              <div className="table-toolbar-left">
                <Typography variant="body2" color="text.secondary">
                  {activeBeaconCount} active {activeBeaconCount === 1 ? 'beacon' : 'beacons'}
                </Typography>
              </div>
              <IconButton
                size="small"
                onClick={() => setDetailModal('beacons')}
                title={`Active beacons (${activeBeaconCount})`}
                aria-label="View active beacons"
              >
                <span className="material-icons-outlined" style={{ fontSize: 18 }}>nfc</span>
              </IconButton>
            </div>

            {devicesLoading && (
              <div className="tab-placeholder">
                <CircularProgress size={24} />
                <div style={{ marginTop: 8 }}>Loading devices…</div>
              </div>
            )}

            {devicesError && (
              <div className="tab-placeholder" style={{ color: 'var(--danger)' }}>
                Failed to load devices.
              </div>
            )}

            {!devicesLoading && !devicesError && deviceDetails.length === 0 && (
              <div className="tab-placeholder">
                No devices assigned to this location.
              </div>
            )}

            {!devicesLoading && !devicesError && deviceDetails.length > 0 && (
              <div className="device-card-grid">
                {deviceDetails.map(d => renderDeviceCard(d))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'digests' && (
          <div>
            <div className="table-toolbar">
              <div className="table-toolbar-left" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Typography variant="body2" color="text.secondary">
                  {digests.length === 0
                    ? 'No digests yet'
                    : `Showing ${digests.length} ${digests.length === 1 ? 'digest' : 'digests'}`}
                </Typography>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                  />
                  Show archived
                </label>
              </div>
              <RouterLink
                to={`/edge_account_digests?location_id=${locationId}`}
                style={{ textDecoration: 'none' }}
              >
                <button className="btn btn-outlined">
                  <span className="material-icons-outlined">open_in_new</span> Manage in Digests Admin
                </button>
              </RouterLink>
            </div>

            {digests.length === 0 ? (
              <div className="tab-placeholder">No digests have been generated for this location yet.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Sections</th>
                      <th>Recipient</th>
                      <th>Approved</th>
                      <th>Sent</th>
                      <th>Opened</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...digests]
                      .sort((a, b) => (b.digest_date || '').localeCompare(a.digest_date || ''))
                      .map(d => (
                        <DigestRow key={d.id} digest={d} onPreview={() => setPreviewDigestId(d.id)} />
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'orders' && (
          <div>
            {fulfillmentLoading && (
              <div className="tab-placeholder">
                <CircularProgress size={24} />
                <div style={{ marginTop: 8 }}>Loading orders…</div>
              </div>
            )}

            {fulfillmentError && (
              <div className="tab-placeholder" style={{ color: 'var(--danger)' }}>
                Failed to load orders.
              </div>
            )}

            {!fulfillmentLoading && !fulfillmentError && fulfillmentResp && !fulfillmentResp.account_linked && (
              <div className="tab-placeholder">
                This location has no HexaHQ account linked. Orders can't be shown.
              </div>
            )}

            {!fulfillmentLoading && !fulfillmentError && fulfillmentResp?.account_linked && (
              <>
                {fulfillmentResp.degraded && (
                  <div className="tab-banner" style={{ background: 'var(--warning-bg, #fff8e1)', color: 'var(--warning-text, #8a6d00)', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 12.5 }}>
                    Order attribution temporarily unavailable (HQ replica) — showing on-hand only.
                  </div>
                )}
                {fulfillmentResp.truncated && (
                  <div className="tab-banner" style={{ background: 'var(--warning-bg, #fff8e1)', color: 'var(--warning-text, #8a6d00)', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 12.5 }}>
                    Showing a partial beacon roster — too many tags to list in full.
                  </div>
                )}
                <div className="text-muted" style={{ fontSize: 11, marginBottom: 8 }}>
                  Last {fulfillmentResp.window_days} days · as of {fmtDateTime(fulfillmentResp.as_of)}
                </div>
                {fulfillmentResp.orders.length > 0
                  ? fulfillmentResp.orders.map(o => <FulfillmentOrderCard key={o.hexahq_order_id} order={o} />)
                  : !fulfillmentResp.degraded && (
                      // Suppress when degraded: orders is empty because attribution
                      // is down (banner above says so), not because none exist.
                      <div className="tab-placeholder">No orders for this account in the last {fulfillmentResp.window_days} days.</div>
                    )}
              </>
            )}
          </div>
        )}

        {activeTab === 'quotes' && (
          <div>
            {openQuotesLoading && (
              <div className="tab-placeholder">
                <CircularProgress size={24} />
                <div style={{ marginTop: 8 }}>Loading open quotes…</div>
              </div>
            )}

            {openQuotesError && (
              <div className="tab-placeholder" style={{ color: 'var(--danger)' }}>
                Failed to load open quotes.
              </div>
            )}

            {!openQuotesLoading && !openQuotesError && openQuotes.length === 0 && (
              <div className="tab-placeholder">
                No open quotes for this location. Quotes in <em>draft</em> or <em>validated</em> status appear here.
              </div>
            )}

            {!openQuotesLoading && !openQuotesError && openQuotes.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: 28 }}></th>
                      <th>Quote ID</th>
                      <th>Status</th>
                      <th>Source</th>
                      <th style={{ textAlign: 'right' }}>Line Items</th>
                      <th>Requested Delivery</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openQuotes.map(q => (
                      <OpenQuoteRow
                        key={q.id}
                        quote={q}
                        expanded={expandedQuotes.has(q.id)}
                        onToggle={() => toggleQuoteExpanded(q.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
      >
        <Box>
          {snackbar && (
            <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ width: '100%' }}>
              {snackbar.message}
            </Alert>
          )}
        </Box>
      </Snackbar>

      {/* Digest email preview modal (reused from EdgeAccountDigestsPage) */}
      <EmailPreviewModal
        open={previewDigestId !== null}
        onClose={() => setPreviewDigestId(null)}
        digestId={previewDigestId}
      />

      {/* Detail modals opened from KPI cards */}
      <KpiDetailModal
        open={detailModal !== null}
        onClose={() => setDetailModal(null)}
        kind={detailModal}
        assignments={assignments}
        activeBeacons={activeBeacons}
        peGroups={peGroups}
        ungrouped={ungrouped}
        skuToPe={skuToPe}
        hvaData={hvaRate}
        emailData={emailOpens}
      />
    </div>
  );
};

export default LocationDashboardPage;
